/**
 * Post-process HTML to auto-linkify file paths in agent messages.
 * Detects absolute and relative file paths and wraps them in clickable links
 * that open the Code Viewer panel.
 *
 * Must run AFTER renderMarkdown() and BEFORE dangerouslySetInnerHTML.
 */

/**
 * Matches file paths like:
 *   /home/akrsmv/projects/voice-assistant/frontend/src/App.tsx
 *   /home/akrsmv/projects/voice-assistant/frontend/src/App.tsx:612
 *   voice-assistant/frontend/src/components/ChatArea.tsx:480
 *   ./src/utils/renderMarkdown.ts
 *   ../backend/src/voice/voice.gateway.ts:269
 *
 * Requires at least one `/` separator and a file extension.
 * Optional :lineNumber suffix.
 */
const FILE_PATH_RE = /(?<!\w)((?:\/home\/akrsmv\/projects\/|\.\.?\/)?(?:[\w@.-]+\/)+[\w@.-]+\.(?:ts|tsx|js|jsx|css|html|json|yaml|yml|md|py|go|rs|sh|sql|toml|env|conf|caddy|hbs|vue|svelte|java|kt|rb|php|c|h|cpp|hpp|txt|xml|csv|lock)(?::(\d+))?)(?!\w)/g;

/**
 * Process rendered HTML string and wrap detected file paths with clickable links.
 * Skips paths that are already inside <a> tags or <code> blocks containing shell commands.
 */
export function linkifyFilePaths(html: string): string {
  // Don't process if no paths likely exist
  if (!html.includes('/') && !html.includes('.ts') && !html.includes('.js')) return html;

  // Use a DOM parser to avoid mangling tags
  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
  const root = doc.body.firstElementChild!;

  // Walk text nodes only
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      const parent = node.parentElement;
      if (!parent) return NodeFilter.FILTER_REJECT;
      // Skip if already inside a link or inside an inline <code>
      const tag = parent.tagName.toLowerCase();
      if (tag === 'a' || tag === 'code' || tag === 'pre') return NodeFilter.FILTER_REJECT;
      // Skip if ancestor is <pre> (code block)
      if (parent.closest('pre')) return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    },
  });

  const textNodes: Text[] = [];
  let node: Node | null;
  while ((node = walker.nextNode())) {
    textNodes.push(node as Text);
  }

  for (const textNode of textNodes) {
    const text = textNode.textContent || '';
    FILE_PATH_RE.lastIndex = 0;
    if (!FILE_PATH_RE.test(text)) continue;

    // Reset and build replacement
    FILE_PATH_RE.lastIndex = 0;
    const frag = doc.createDocumentFragment();
    let lastIdx = 0;
    let match: RegExpExecArray | null;

    while ((match = FILE_PATH_RE.exec(text)) !== null) {
      // Add text before match
      if (match.index > lastIdx) {
        frag.appendChild(doc.createTextNode(text.slice(lastIdx, match.index)));
      }

      const fullMatch = match[1];
      const lineNum = match[2] || '';
      const pathOnly = lineNum ? fullMatch.replace(`:${lineNum}`, '') : fullMatch;

      const link = doc.createElement('a');
      link.className = 'file-link';
      link.setAttribute('data-path', pathOnly);
      if (lineNum) link.setAttribute('data-line', lineNum);
      link.setAttribute('href', '#');
      link.setAttribute('title', `Open ${pathOnly}${lineNum ? ` at line ${lineNum}` : ''}`);
      link.textContent = fullMatch;

      frag.appendChild(link);
      lastIdx = match.index + match[0].length;
    }

    // Remaining text after last match
    if (lastIdx < text.length) {
      frag.appendChild(doc.createTextNode(text.slice(lastIdx)));
    }

    if (lastIdx > 0) {
      textNode.replaceWith(frag);
    }
  }

  // Second pass: linkify <code> elements whose entire text content is a single file path.
  // e.g. `voice-assistant/plans/foo.md` → clickable link.
  // Skips <code> inside <pre> (code blocks) and already-linked paths.
  const codeEls = Array.from(root.querySelectorAll('code'));
  for (const code of codeEls) {
    if (code.closest('pre') || code.closest('a')) continue;
    const text = (code.textContent || '').trim();
    FILE_PATH_RE.lastIndex = 0;
    const match = FILE_PATH_RE.exec(text);
    // Only replace when the match covers the entire text (nothing else in the code span)
    if (!match || match.index !== 0 || match[0].length !== text.length) continue;

    const pathOnly = match[2] ? match[1].replace(`:${match[2]}`, '') : match[1];
    const lineNum = match[2] || '';

    const link = doc.createElement('a');
    link.className = 'file-link';
    link.setAttribute('data-path', pathOnly);
    if (lineNum) link.setAttribute('data-line', lineNum);
    link.setAttribute('href', '#');
    link.setAttribute('title', `Open ${pathOnly}${lineNum ? ` at line ${lineNum}` : ''}`);
    link.textContent = text;
    code.replaceWith(link);
  }

  return root.innerHTML;
}
